import React, { ChangeEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { createTopUpAPI } from 'services/topUpsApi';
import { CustomCard } from 'components/CustomCard';
import { toastSuccess, toastWarning } from 'utils/toastMethod';
import Input from 'components/Input';
import PageLayout from 'components/Layout/PageLayout';

export default function TopUpForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialOrderId = searchParams.get('orderId') ?? '';

  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [orderIds, setOrderIds] = useState<string[]>([initialOrderId]);
  const [amountError, setAmountError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');

  const handleOrderIdChange = (index: number, value: string) => {
    setOrderIds((prev) => prev.map((id, i) => (i === index ? value : id)));
  };

  const addOrderId = () => setOrderIds((prev) => [...prev, '']);

  const removeOrderId = (index: number) => {
    setOrderIds((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSubmit = () => {
    const parsedAmount = Number(amount.replace(',', '.'));
    if (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setAmountError('Inserire un importo valido maggiore di zero.');
      return;
    }
    setAmountError('');

    const cleanedOrderIds = orderIds.map((id) => id.trim()).filter((id) => id.length > 0);

    setIsSubmitting(true);
    createTopUpAPI({
      amount: Math.round(parsedAmount * 100),
      notes,
      orderIds: cleanedOrderIds,
    })
      .then((response) => {
        toastSuccess('Ricarica creata correttamente.');
        if (response?.checkoutUrl) {
          setCheckoutUrl(response.checkoutUrl);
        } else {
          navigate('/topups');
        }
      })
      .catch(() => toastWarning('Qualcosa è andato storto!'))
      .finally(() => setIsSubmitting(false));
  };

  const handleCopyCheckoutUrl = () => {
    navigator.clipboard
      .writeText(checkoutUrl)
      .then(() => toastSuccess('Link copiato negli appunti.'))
      .catch(() => toastWarning('Impossibile copiare il link.'));
  };

  const handleOpenCheckoutUrl = () => {
    window.open(checkoutUrl, '_blank');
  };

  const handleDismissModal = () => {
    setCheckoutUrl('');
    navigate('/topups');
  };

  return (
    <PageLayout
      titlePage={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => navigate('/topups')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 4px',
              fontSize: '28px',
              fontWeight: 600,
              lineHeight: 1,
            }}
            aria-label="Torna alla lista ricariche"
          >
            &#8592;
          </button>
          <span>Nuova ricarica</span>
        </div>
      }
    >
      <div style={{ maxWidth: '480px' }}>
        <CustomCard title={<h3>Dati ricarica</h3>} width="100%">
          <Input
            title="Importo (€)"
            text={amount}
            textError={amountError}
            placeholder="Es. 50,00"
            handleChange={(event: ChangeEvent<HTMLInputElement>) => setAmount(event.target.value)}
          />
          <Input
            title="Note"
            text={notes}
            placeholder="Note (opzionale)"
            handleChange={(event: ChangeEvent<HTMLInputElement>) => setNotes(event.target.value)}
          />

          <div className="input-container">
            <label className="input-label">Ordini collegati</label>
            {orderIds.map((orderId, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input
                  className="input-box"
                  value={orderId}
                  onChange={(event) => handleOrderIdChange(index, event.target.value)}
                  placeholder="ID ordine (opzionale)"
                />
                {orderIds.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeOrderId(index)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#ff0000',
                    }}
                    aria-label="Rimuovi ordine"
                  >
                    &times;
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              onClick={addOrderId}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontWeight: 600,
                color: '#004159',
              }}
            >
              + Aggiungi ordine
            </button>
          </div>

          <button
            className="btn btn-primary"
            style={{
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              marginTop: '16px',
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              color: '#ffffff',
            }}
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Creazione in corso...' : 'Crea ricarica'}
          </button>
        </CustomCard>
      </div>

      <Modal show={!!checkoutUrl} onHide={handleDismissModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Checkout ricarica</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Ricarica creata correttamente. Utilizza il link seguente per completare il pagamento:</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input className="input-box" style={{ flex: 1 }} value={checkoutUrl} readOnly />
            <Button variant="outline-secondary" onClick={handleCopyCheckoutUrl}>
              Copia
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDismissModal}>
            Chiudi
          </Button>
          <Button variant="primary" onClick={handleOpenCheckoutUrl}>
            Apri in una nuova finestra
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
